// @flow

import { get } from 'lodash';
import memoizeOne from 'memoize-one';
import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ThemeContext, Icon } from 'react-native-elements';
import { Text } from 'react-native';
import SectionedMultiSelect from 'react-native-sectioned-multi-select';
import * as Yup from 'yup';
import FieldWrapper from './FieldWrapper';
import { useFormattedFieldErrors } from './utils';

const styles = memoizeOne((theme) => ({
    label: {
        fontSize: theme.fontsizes.categoryTitle,
        color: theme.colors.defaultTextColor,
        marginHorizontal: 10,
        fontWeight: 'bold',
        marginBottom: -10,
    },
    error: {
        color: theme.colors.danger,
        marginTop: -20,
        marginHorizontal: 20,
        fontSize: theme.fontsizes.textSmall,
    },
    selectedItems: {
        width: '90%',
        fontSize: theme.fontsizes.text,
        borderWidth: 1,
        borderRadius: 1,
        paddingVertical: 5,
        paddingHorizontal: 10,
        marginHorizontal: 10,
        backgroundColor: theme.colors.background,
    },
    selectedEmpty: {
        borderColor: theme.colors.defaultTextColor,
        color: theme.colors.defaultTextColor,
    },
    selectedNotEmpty: {
        borderColor: theme.colors.defaultTextColor,
        color: theme.colors.defaultTextColor,
    },
    container: {
        marginHorizontal: 250,
        marginVertical: 50,
        backgroundColor: theme.colors.background,
    },
    item: {
        paddingHorizontal: 20,
        fontSize: 50,
        backgroundColor: 'transparent',
    },
    itemText: {
        color: theme.colors.defaultTextColor,
        fontSize: theme.fontsizes.categoryTitle,
    },
    selectedItemText: {
        color: theme.colors.success,
    },
    searchTextInput: {
        fontSize: theme.fontsizes.categoryTitle,
    },
    confirmText: {
        color: theme.colors.defaultTextColor,
        fontSize: theme.fontsizes.screenTitle,
    },
    color: {
        color: theme.colors.defaultTextColor,
    },
}));

type Props = {
    field: {
        value: any,
        name: string,
    },
    form: {
        errors: Object,
        touched: Object,
        isSubmitting: boolean,
        handleChange: (name: string) => (value: string | null) => void,
        handleBlur: (name: string) => () => void,
        setFieldValue: (name: string, value: string) => () => void,
    },
    required: boolean,
    label: string,
    help_text?: string,
    disabled?: boolean,
    choices: Object,
    many: boolean,
};

const messages = defineMessages({
    initialValueMessage: {
        id: 'select-initial-value',
        defaultMessage: 'No',
    },
    required: {
        id: 'baseSelect-required',
        defaultMessage: 'This field is required.',
    },
    confirm: {
        id: 'baseSelect-confirm',
        defaultMessage: 'Confirm',
    },
    search: {
        id: 'baseSelect-search',
        defaultMessage: 'Search',
    },
});

const choicesValues = memoizeOne(Object.keys);
const processChoices = memoizeOne((choices) => choicesValues(choices).map((key) => ({ id: key, name: choices[key] })));
const colors = memoizeOne((theme) => ({
    primary: theme.colors.success,
    success: theme.colors.success,
    cancel: theme.colors.background,
    itemBackground: 'transparent',
}));

const BaseSelect = (props: Props) => {
    const {
        disabled,
        field: {
            name, value,
        },
        form: {
            errors, touched,
            handleChange, handleBlur,
        },
        label,
        help_text: helpText,
        required,
        choices,
        many,
    } = props;

    const { theme } = React.useContext(ThemeContext);

    let errs = get(errors, name);
    errs = useFormattedFieldErrors(errs);

    const { formatMessage } = useIntl();

    const [selectedItems, setSelectedItems] = React.useState([]);
    const itemsArr = processChoices(choices);

    const onSelect = React.useCallback((multiSelectedItems) => {
        setSelectedItems(multiSelectedItems);
        handleChange(name)(many ? multiSelectedItems : multiSelectedItems[0]);
        handleBlur(name);
    }, [handleBlur, handleChange, many, name]);

    const onCancel = React.useCallback(() => {
        onSelect([]);
    }, []);

    const renderSelectText = React.useCallback(() => {
        let selectText = '';

        if (selectedItems.length > 0) {
            selectText = selectedItems.map((key, index) => {
                if (index < selectedItems.length - 1) {
                    return `${choices[key]} , `;
                }
                return choices[key];
            }).join('');
            return <Text style={[styles(theme).selectedItems, styles(theme).selectedNotEmpty]}>{selectText}</Text>;
        }
        selectText = `${formatMessage(messages.initialValueMessage)} ${label}`;
        return <Text style={[styles(theme).selectedItems, styles(theme).selectedEmpty]}>{selectText}</Text>;
    }, [choices, formatMessage, label, selectedItems, theme]);

    return (
        <FieldWrapper
            required={required}
            helpText={helpText}
        >
            <Text style={styles(theme).label}>{required ? `${label} *` : label}</Text>

            <SectionedMultiSelect
                items={itemsArr}
                uniqueKey="id"
                single={!many}
                modalWithTouchable
                modalWithSafeAreaView
                showChips={false}
                confirmText={formatMessage(messages.confirm)}
                searchPlaceholderText={`${formatMessage(messages.search)} ${label}`}
                renderSelectText={renderSelectText}
                selectedItems={selectedItems}
                showCancelButton
                onSelectedItemsChange={onSelect}
                onCancel={onCancel}
                cancelIconComponent={(
                    <Icon
                        type="material"
                        name="cancel"
                        iconStyle={styles(theme).color}
                    />
                )}
                colors={colors(theme)}
                styles={styles(theme)}
                selectToggleIconComponent={(
                    <Icon
                        type="material"
                        name="keyboard-arrow-down"
                        iconStyle={styles(theme).color}
                    />
                )}
            />

            {touched[name] && errs ? <Text style={styles(theme).error}>{errs}</Text> : null}
        </FieldWrapper>
    );
};

BaseSelect.getValidationSchema = (field: { validation: Object }) => {
    if (field.validation.required) {
        return Yup.string().required(messages.required).nullable();
    }

    return Yup.string().nullable();
};

BaseSelect.defaultProps = {
    disabled: false,
    help_text: '',
};

export default BaseSelect;
